import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { authorizedAxiosInstance } from '@/clients';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

export interface DatasetSchemaField {
    key: string;
    label?: string;
    type?: string;
    icon?: string;
    description?: string;
    metricsCount?: number;
    dimensionsCount?: number;
    options?: string[];
    defaultMetric?: {
        field: string;
        label: string;
        aggregation?: 'sum' | 'avg' | 'min' | 'max' | 'count';
    };
    defaultDimension?: {
        field: string;
        label: string;
    };
}

export interface NormalizedDatasetSchema {
    datasets: DatasetSchemaField[];
    metrics: DatasetSchemaField[];
    dimensions: DatasetSchemaField[];
    filters: DatasetSchemaField[];
    time?: {
        field?: string;
        granularity: string[];
        supported: boolean;
    };
    requiresProvider?: unknown;
}

export const normalizeDatasetSchema = (schema: unknown): NormalizedDatasetSchema => {
    const rawSchema = schema as Partial<NormalizedDatasetSchema> | undefined;

    return {
        datasets: rawSchema?.datasets ?? [],
        metrics: rawSchema?.metrics ?? [],
        dimensions: rawSchema?.dimensions ?? [],
        filters: rawSchema?.filters ?? [],
        time: rawSchema?.time,
        requiresProvider: rawSchema?.requiresProvider,
    };
};

type UseDatasetSchemaOptions = {
    includeCategory?: boolean;
};

const DATASET_SCHEMA_QUERY_VERSION = 4;

export const useDatasetSchema = (draft: Partial<ReportComponentConfig>, options: UseDatasetSchemaOptions = {}) => {
    const { includeCategory = true } = options;

    const type = draft?.dataSet?.type;
    const category = includeCategory ? draft?.dataSet?.key : undefined;
    const providerId = draft?.dataSet?.providerId;
    const params = useMemo(
        () => ({
            type,
            category,
            providerId,
        }),
        [category, providerId, type],
    );

    const { data, isLoading, error } = useQuery({
        queryKey: ['datasetSchema', DATASET_SCHEMA_QUERY_VERSION, type, category ?? null, providerId ?? null],
        queryFn: async () => {
            const res = await authorizedAxiosInstance.get(`/api/datasets/schema`, {
                params,
            });

            return res.data;
        },
        enabled: !!type,
        staleTime: 5 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const normalized = useMemo(() => normalizeDatasetSchema(data), [data]);

    return {
        schema: normalized,
        isLoading,
        error,
    };
};
